import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "Nigeria", address: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (!form.address || form.address.trim().length < 5) { toast.error("Please enter your full delivery address"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to AfriMarket");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not register");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-2xl mb-8 inline-block">AfriMarket</Link>
          <h1 className="font-display text-3xl mb-2">Create your account</h1>
          <p className="text-muted-foreground text-sm mb-8">Join the marketplace and start buying, selling and connecting.</p>

          <Button type="button" variant="outline" onClick={handleGoogle} className="w-full h-12 rounded-md border-black/20 mb-4" data-testid="google-signup-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/10"/><span className="text-xs text-muted-foreground">or with email</span><div className="flex-1 h-px bg-black/10"/>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="label-overline">Full name</Label>
              <Input data-testid="register-name" value={form.name} onChange={(e) => upd("name", e.target.value)} required className="mt-2"/>
            </div>
            <div>
              <Label className="label-overline">Email</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required className="mt-2"/>
            </div>
            <div>
              <Label className="label-overline">Password</Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required minLength={6} className="mt-2"/>
            </div>
            <div>
              <Label className="label-overline">Country</Label>
              <Input data-testid="register-country" value={form.country} onChange={(e) => upd("country", e.target.value)} className="mt-2"/>
            </div>
            <div>
              <Label className="label-overline">Delivery address</Label>
              <textarea
                data-testid="register-address"
                value={form.address}
                onChange={(e) => upd("address", e.target.value)}
                required
                rows={3}
                placeholder="House number, street, city, state/region, postcode"
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
              />
              <p className="text-xs text-muted-foreground mt-1">We use this to deliver your orders. You can update it anytime in your dashboard.</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md" data-testid="register-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Already a member? <Link to="/login" className="text-[#C05A3C] font-medium hover:underline" data-testid="link-login">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&q=80&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-[#C05A3C]/30"/>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="label-overline text-white/80 mb-3">Join us</div>
          <h2 className="font-display text-4xl leading-tight">The diaspora&apos;s home for African products and services.</h2>
        </div>
      </div>
    </div>
  );
};

export default Register;
